import { LegalPageLayout } from "./legal/LegalPageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, MessageSquare, Phone, HelpCircle, Book, Users } from "lucide-react";
import { SEO } from "@/components/SEO";

const Support = () => {
  return (
    <>
      <SEO 
        title="Support — Nous Sommes Là Pour Vous Aider"
        description="Contactez l'équipe Libr'Addict pour toute question ou assistance. Support disponible par email et téléphone."
      />
      <LegalPageLayout title="Support et Assistance">
        <div className="space-y-8 animate-fade-in">
          {/* Introduction */}
          <Card className="border border-primary/20 bg-card">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl">Comment Pouvons-Nous Vous Aider ?</CardTitle>
              <CardDescription className="text-lg mt-2">
                Notre équipe est à votre disposition pour répondre à toutes vos questions
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Contact Methods */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Email Support */}
            <Card className="hover-scale">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-8 h-8 text-primary" />
                  <CardTitle className="text-xl">Email</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Pour toute question générale, technique ou administrative
                </p>
                <div className="bg-primary/10 p-4 rounded-lg">
                  <a 
                    href="mailto:contact@libraddict.fr" 
                    className="text-lg font-semibold text-primary hover:underline"
                  >
                    contact@libraddict.fr
                  </a>
                </div>
                <p className="text-sm text-muted-foreground">
                  Réponse sous 24-48h ouvrées
                </p>
              </CardContent>
            </Card>

            {/* Phone Support */}
            <Card className="hover-scale">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Phone className="w-8 h-8 text-accent" />
                  <CardTitle className="text-xl">Téléphone</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Pour les questions urgentes ou assistance immédiate
                </p>
                <div className="bg-accent/10 p-4 rounded-lg">
                  <p className="text-lg font-semibold text-foreground">
                    Bientôt disponible
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Horaires : Lun-Ven 9h-18h
                </p>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Categories */}
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <HelpCircle className="w-7 h-7 text-primary" />
                <CardTitle className="text-2xl">Questions Fréquentes</CardTitle>
              </div>
              <CardDescription>
                Consultez nos guides selon votre profil
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded-lg hover:border-primary transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-6 h-6 text-primary" />
                    <h4 className="font-semibold">Pour les Patients</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Comment prendre rendez-vous ?</li>
                    <li>• Comment annuler un rendez-vous ?</li>
                    <li>• La téléconsultation est-elle remboursée ?</li>
                    <li>• Comment accéder aux ressources RDR ?</li>
                  </ul>
                </div>

                <div className="p-4 border border-border rounded-lg hover:border-accent transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <Book className="w-6 h-6 text-accent" />
                    <h4 className="font-semibold">Pour les Praticiens</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Comment créer mon profil ?</li>
                    <li>• Comment gérer mes disponibilités ?</li>
                    <li>• Quels sont les tarifs ?</li>
                    <li>• Comment utiliser la téléconsultation ?</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technical Support */}
          <Card className="border border-accent/30 bg-card">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <MessageSquare className="w-7 h-7 text-accent" />
                <CardTitle className="text-xl">Support Technique</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Pour tout problème technique (connexion, bugs, performance), décrivez votre problème en détail :
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground ml-4">
                <li>• Type d'appareil (ordinateur, smartphone, tablette)</li>
                <li>• Navigateur utilisé (Chrome, Safari, Firefox...)</li>
                <li>• Description du problème</li>
                <li>• Captures d'écran si possible</li>
              </ul>
              <Button 
                className="w-full md:w-auto"
                onClick={() => window.location.href = 'mailto:contact@libraddict.fr?subject=Support Technique'}
              >
                <Mail className="w-4 h-4 mr-2" />
                Contacter le support technique
              </Button>
            </CardContent>
          </Card>

          {/* Emergency */}
          <Card className="border border-destructive/30 bg-card">
            <CardContent className="pt-8 space-y-4">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-destructive mb-2">En Cas d'Urgence</h3>
                <p className="text-muted-foreground mb-4">
                  Si vous êtes en situation de crise ou d'urgence médicale, contactez immédiatement :
                </p>
                <div className="space-y-2">
                  <p className="text-lg font-semibold">SAMU : 15</p>
                  <p className="text-lg font-semibold">Urgences : 112</p>
                  <p className="text-lg font-semibold">Drogues Info Service : 0 800 23 13 13</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Office Hours */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-xl">Horaires du Support</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Support Email</h4>
                  <p className="text-sm text-muted-foreground">
                    7 jours sur 7<br />
                    Réponse sous 24-48h ouvrées
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Support Téléphonique</h4>
                  <p className="text-sm text-muted-foreground">
                    Lundi - Vendredi<br />
                    9h00 - 18h00 (bientôt disponible)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default Support;
